import * as Papa from "papaparse";

import { DEAL_SIGNAL_CSV_HEADERS, DEAL_SIGNAL_MAX_COMPANIES } from "../constants";
import type { DealSignalCsvValidation } from "../types/model";

const INVALID_RESULT: DealSignalCsvValidation = { isValid: false, reason: "invalidFormat", companiesCount: 0 };

const NO_DATA_ROWS_RESULT: DealSignalCsvValidation = { isValid: false, reason: "noDataRows", companiesCount: 0 };

const [COMPANY_HEADER] = DEAL_SIGNAL_CSV_HEADERS;

const normalizeHeader = (header: string): string => header.trim().toLowerCase();

const hasExactHeaders = (fields: readonly string[] | undefined): boolean => {
    if (!fields || fields.length !== DEAL_SIGNAL_CSV_HEADERS.length) {
        return false;
    }

    const normalized = new Set(fields.map(normalizeHeader));

    return (
        normalized.size === DEAL_SIGNAL_CSV_HEADERS.length &&
        DEAL_SIGNAL_CSV_HEADERS.every((header) => normalized.has(header))
    );
};

/**
 * Pre-flight check on the picked file: the columns are the ones the backend needs, and how many distinct
 * companies it covers.
 *
 * Streamed row by row rather than parsed whole. `step` is what makes papaparse chunk the read at all —
 * without it the file is materialised as one string and one object per row, to learn two facts. It also
 * lets a wrong header abort after the first row instead of parsing everything before rejecting it.
 *
 * Companies are counted distinct, not rows: a deals export can carry several rows per company, and the
 * server reports back in companies — a preview measuring something else would just look like a bug.
 */
export const validateDealSignalCsv = (file: File, signal?: AbortSignal): Promise<DealSignalCsvValidation> => {
    return new Promise((resolve) => {
        const companies = new Set<string>();
        // `step` never fires for a header-only file, and `complete` carries no `meta.fields` in streaming
        // mode, so the header row is captured here — by index, because papaparse visits it more than once.
        const headerFields: string[] = [];
        let hasSeenRow = false;
        let hasValidHeaders = false;
        let companyField: string | undefined;

        Papa.parse<Record<string, string>>(file, {
            header: true,
            skipEmptyLines: true,

            transformHeader(header, index) {
                headerFields[index] = header;

                return header;
            },

            step({ data, meta }, parser) {
                if (signal?.aborted) {
                    parser.abort();

                    return;
                }

                if (!hasSeenRow) {
                    hasSeenRow = true;
                    hasValidHeaders = hasExactHeaders(meta.fields);

                    if (!hasValidHeaders) {
                        parser.abort();

                        return;
                    }

                    companyField = meta.fields?.find((field) => normalizeHeader(field) === COMPANY_HEADER);
                }

                const company = companyField ? data[companyField] : undefined;

                if (company?.trim()) {
                    companies.add(company.trim().toLowerCase());
                }
            },

            complete() {
                const headersAreValid = hasSeenRow ? hasValidHeaders : hasExactHeaders(headerFields);

                if (!headersAreValid) {
                    resolve(INVALID_RESULT);

                    return;
                }

                // The columns are right but there is nothing under them — a header-only export, or one
                // whose company column came out blank. That is a different mistake from a wrong format.
                if (companies.size === 0) {
                    resolve(NO_DATA_ROWS_RESULT);

                    return;
                }

                // Counted to the end rather than aborted at the limit: the message names the file's own
                // total, so knowing "more than 2,000" is not enough.
                if (companies.size > DEAL_SIGNAL_MAX_COMPANIES) {
                    resolve({ isValid: false, reason: "tooManyCompanies", companiesCount: companies.size });

                    return;
                }

                resolve({ isValid: true, companiesCount: companies.size });
            },

            error() {
                resolve(INVALID_RESULT);
            },
        });
    });
};
